/**
 * Product Catalogue — Server-Side Fetch Module (Hybrid)
 *
 * Initial page of 12 products is server-rendered by Liquid.
 * This JS only fetches from the server when the user:
 *   - selects a filter / sort / tag
 *   - types a search query
 *   - clicks "Meer laden" (load more)
 *
 * Fetch endpoint: /collections/sale-selection?view=api
 * Depends on: GSAP + ScrollTrigger (loaded globally via CDN).
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════════════
     CONFIGURATION
     ══════════════════════════════════════════════════ */

  /*
   * Fallback only. The active collection is read from the catalogue-meta block
   * and held in state.collection, so the All products page can switch it at
   * runtime. It used to be hardcoded here, which meant the module could only
   * ever list one collection.
   */
  var DEFAULT_COLLECTION_HANDLE = 'sale-selection';
  var PAGE_SIZE = 12;
  var SEARCH_DEBOUNCE_MS = 350;

  /* Sort value mapping: internal key → Shopify sort_by param */
  var SORT_MAP = {
    'default': '',
    'price-asc': 'price-ascending',
    'price-desc': 'price-descending',
    'title-asc': 'title-ascending',
    'title-desc': 'title-descending'
  };

  /*
   * STATUS_MAP removed. It mapped the status slugs to capitalised metafield
   * values for a server-side filter that never worked — see buildFetchUrl.
   * Status is now matched against the data-status attribute, which uses the
   * lowercase slugs directly.
   */

  /* ══════════════════════════════════════════════════
     STATE
     ══════════════════════════════════════════════════ */

  var state = {
    filters: { type: '', vendor: '', status: '' },
    /*
     * Filters configured in the Search & Discovery app — Categorie, Merk, and
     * anything added later. Keyed by Shopify's own param name, e.g.
     * { "filter.p.vendor": "Minotti" }, so the theme does not need to know what
     * each filter is. `filters.status` above stays separate: it is derived from
     * a metafield plus availability and is handled client-side.
     */
    shopifyFilters: {},
    sort: 'default',
    tag: '',
    searchQuery: '',
    collection: DEFAULT_COLLECTION_HANDLE,
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    isLoading: false,
    isLoadingMore: false,
    abortController: null
  };

  /* ══════════════════════════════════════════════════
     DOM REFERENCES
     ══════════════════════════════════════════════════ */

  var grid = document.getElementById('catalogue-grid');
  var loadMoreWrapper = document.getElementById('catalogue-load-more-wrapper');
  /*
   * "Meer laden" is rendered by the hora-button-1 snippet with no `link`, and
   * that snippet only outputs an <a> when a link is given — otherwise a <div>.
   * So querySelector('a, button') returned null here and the click listener was
   * never attached, which is why only the first 12 products ever appeared.
   * Fall back to the button element, then to the wrapper itself.
   */
  var loadMoreBtn = loadMoreWrapper
    ? (loadMoreWrapper.querySelector('a, button') ||
       loadMoreWrapper.querySelector('.hora-button-1') ||
       loadMoreWrapper)
    : null;
  var countEl = document.querySelector('[data-count]');
  var searchInput = document.querySelector('.sale-selection-input');
  var tagButtons = document.querySelectorAll('.sale-tag');
  var filterSelects = Array.from(document.querySelectorAll('[data-filter-select]'));
  var sortSelect = document.querySelector('[data-sort-select]');

  if (!grid) return; /* catalogue not on this page */

  /* ══════════════════════════════════════════════════
     READ INITIAL STATE FROM SERVER-RENDERED META
     ══════════════════════════════════════════════════ */

  var metaEl = document.getElementById('catalogue-meta');
  if (metaEl) {
    try {
      var initMeta = JSON.parse(metaEl.textContent);
      state.currentPage = initMeta.currentPage || 1;
      state.totalPages = initMeta.totalPages || 1;
      state.totalProducts = initMeta.totalProducts || 0;
      state.collection = initMeta.collectionHandle || DEFAULT_COLLECTION_HANDLE;
    } catch (e) {
      console.warn('[catalogue-api] Could not parse initial meta:', e);
    }
  }

  /* ══════════════════════════════════════════════════
     SKELETON HELPERS
     ══════════════════════════════════════════════════ */

  var SKELETON_HTML =
    '<div class="skeleton-card__image skeleton-pulse"></div>' +
    '<div class="skeleton-card__content">' +
    '  <div class="skeleton-card__line skeleton-card__line--title skeleton-pulse"></div>' +
    '  <div class="skeleton-card__line skeleton-card__line--subtitle skeleton-pulse"></div>' +
    '</div>';

  function createSkeleton(extraClass) {
    var el = document.createElement('div');
    el.className = 'product-catalogue__item skeleton-card' + (extraClass ? ' ' + extraClass : '');
    el.innerHTML = SKELETON_HTML;
    return el;
  }

  function showSkeletons(count) {
    grid.innerHTML = '';
    for (var i = 0; i < count; i++) {
      grid.appendChild(createSkeleton());
    }
  }

  function appendSkeletons(count) {
    for (var i = 0; i < count; i++) {
      grid.appendChild(createSkeleton('skeleton-card--append'));
    }
  }

  function removeAppendSkeletons() {
    var els = grid.querySelectorAll('.skeleton-card--append');
    for (var i = 0; i < els.length; i++) els[i].remove();
  }

  /* ══════════════════════════════════════════════════
     URL BUILDER
     ══════════════════════════════════════════════════ */

  function buildFetchUrl(page) {
    var params = [];

    params.push('view=api');

    if (page > 1) params.push('page=' + page);

    /*
     * While a search is active, results come from Shopify's search endpoint
     * rendered by templates/search.api.liquid. Search is store-wide by design,
     * so the collection and the filters are deliberately not applied.
     */
    if (state.searchQuery) {
      params.push('type=product');
      params.push('options[prefix]=last');
      params.push('q=' + encodeURIComponent(state.searchQuery));
      return '/search?' + params.join('&');
    }

    /*
     * Filters configured in Search & Discovery. Shopify supplies both the param
     * name and the value, so they are passed straight through — no mapping in
     * the theme, and a new filter added in the app works without a code change.
     */
    Object.keys(state.shopifyFilters).forEach(function (param) {
      var value = state.shopifyFilters[param];
      if (value) params.push(param + '=' + encodeURIComponent(value));
    });

    /*
     * Product type, used by the Indoor / Outdoor quick-filter buttons. Kept
     * separate from the dropdowns above: type is a different dimension from
     * Category, so both can be applied at once.
     */
    if (state.filters.type) {
      params.push('filter.p.product_type=' + encodeURIComponent(state.filters.type));
    }

    /*
     * Status is deliberately NOT sent to the server.
     *
     * This used to push filter.p.m.custom.product_status, a metafield that does
     * not exist on these products — status is derived from custom.reserved plus
     * product.available. Shopify silently ignores unknown filter params, so
     * every status returned the full, unfiltered list.
     *
     * There is no single native filter that expresses it either: "verkocht" is
     * availability, "gereserveerd" is a metafield. Both templates already stamp
     * data-status on each item, so it is filtered client-side instead — see
     * fetchAllForStatus below.
     */

    /* Tag */
    /*
     * Nothing writes state.tag any more — the quick-filter buttons now use the
     * product Type filter above. Kept so a genuine tag filter can be added
     * later without rebuilding this, but it is inert while state.tag is empty.
     */
    if (state.tag) {
      params.push('filter.p.tag=' + encodeURIComponent(state.tag));
    }

    /* Sort */
    if (state.sort && SORT_MAP[state.sort]) {
      params.push('sort_by=' + SORT_MAP[state.sort]);
    }

    return '/collections/' + (state.collection || DEFAULT_COLLECTION_HANDLE) + '?' + params.join('&');
  }

  /* ══════════════════════════════════════════════════
     COUNT & LOAD-MORE VISIBILITY
     ══════════════════════════════════════════════════ */

  function updateCount() {
    if (!countEl) return;
    var showing = grid.querySelectorAll('.product-catalogue__item:not(.skeleton-card)').length;
    countEl.textContent = showing + ' van ' + state.totalProducts + ' producten';
  }

  function setLoadMoreVisible(show) {
    if (loadMoreWrapper) {
      loadMoreWrapper.style.display = show ? '' : 'none';
    }
  }

  /* ══════════════════════════════════════════════════
     GSAP — Animate freshly injected cards
     ══════════════════════════════════════════════════ */

  function animateNewCards(newElements) {
    if (typeof gsap === 'undefined' || !newElements || !newElements.length) return;

    /* Set invisible */
    gsap.set(newElements, { opacity: 0, y: 50 });

    /* Group by visual row */
    var rows = {};
    newElements.forEach(function (card) {
      var top = Math.round(card.getBoundingClientRect().top + window.scrollY);
      if (!rows[top]) rows[top] = [];
      rows[top].push(card);
    });

    /* Staggered reveal per row */
    Object.keys(rows).forEach(function (key) {
      var rowCards = rows[key];
      gsap.to(rowCards, {
        opacity: 1,
        y: 0,
        duration: 0.75,
        ease: 'power3.out',
        stagger: 0.12,
        scrollTrigger: {
          trigger: rowCards[0],
          start: 'top 90%',
          toggleActions: 'play none none none',
          once: true
        }
      });
    });

    /* Refresh triggers for new DOM height */
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  }

  /* ══════════════════════════════════════════════════
     CART BUTTON BINDING
     ══════════════════════════════════════════════════ */

  function bindCartButtons() {
    /* Cart button clicks are now handled by cart-drawer.js
       via event delegation. Just sync states after new cards load. */
    if (window.HoraCart && window.HoraCart.syncButtons) {
      window.HoraCart.syncButtons();
    }
  }

  /* ══════════════════════════════════════════════════
     CORE FETCH (used for filters / sort / load-more)
     ══════════════════════════════════════════════════ */

  /* Fetch one page and hand back its cards plus metadata. */
  function fetchPage(page, signal) {
    return fetch(buildFetchUrl(page), {
      signal: signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var container = doc.getElementById('api-products');
        var metaEl2 = doc.getElementById('api-meta');
        var meta = { currentPage: page, totalPages: 1, totalProducts: 0, hasNextPage: false };
        if (metaEl2) {
          try { meta = JSON.parse(metaEl2.textContent); } catch (e) { /* keep defaults */ }
        }
        return { cards: container ? Array.from(container.children) : [], meta: meta };
      });
  }

  /*
   * Status filtering.
   *
   * Status cannot be filtered by the server (see buildFetchUrl), so every page
   * is walked once, the matching items are kept in memory, and they are then
   * paged out to the grid in the same PAGE_SIZE chunks as everywhere else.
   * "Meer laden" keeps working — it just serves the next slice from memory
   * instead of making another request.
   *
   * MAX_STATUS_PAGES caps the walk so a very large collection cannot spin
   * forever; at 12 per page that covers 600 products.
   */
  var MAX_STATUS_PAGES = 50;
  var statusPool = [];   /* every matching card, unrendered */
  var statusShown = 0;   /* how many have been placed in the grid */

  function renderStatusSlice(append) {
    var slice = statusPool.slice(statusShown, statusShown + PAGE_SIZE);
    statusShown += slice.length;

    if (!append) grid.innerHTML = '';

    if (!statusPool.length) {
      grid.innerHTML =
        '<div class="product-catalogue__empty">' +
        '  <p>Geen producten gevonden.</p>' +
        '</div>';
      updateCount();
      setLoadMoreVisible(false);
      return;
    }

    var fragment = document.createDocumentFragment();
    var newElements = [];
    slice.forEach(function (card) {
      var imported = document.importNode(card, true);
      newElements.push(imported);
      fragment.appendChild(imported);
    });
    grid.appendChild(fragment);

    /* Mirror the real pagination so the load-more guard behaves identically. */
    state.totalProducts = statusPool.length;
    state.totalPages = Math.ceil(statusPool.length / PAGE_SIZE) || 1;
    state.currentPage = Math.ceil(statusShown / PAGE_SIZE) || 1;

    updateCount();
    setLoadMoreVisible(statusShown < statusPool.length);
    animateNewCards(newElements);
    bindCartButtons();
  }

  function fetchAllForStatus() {
    if (state.abortController) state.abortController.abort();
    state.abortController = new AbortController();
    var signal = state.abortController.signal;

    state.isLoading = true;
    showSkeletons(6);
    setLoadMoreVisible(false);

    var wanted = state.filters.status;
    statusPool = [];
    statusShown = 0;

    function walk(page) {
      return fetchPage(page, signal).then(function (result) {
        result.cards.forEach(function (card) {
          if (card.getAttribute('data-status') === wanted) statusPool.push(card);
        });
        if (result.meta.hasNextPage && page < MAX_STATUS_PAGES) return walk(page + 1);
        return null;
      });
    }

    return walk(1)
      .then(function () { renderStatusSlice(false); })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('[catalogue-api] Status filter error:', err);
        grid.innerHTML =
          '<div class="product-catalogue__empty">' +
          '  <p>Er is iets misgegaan bij het laden.</p>' +
          '</div>';
      })
      .finally(function () {
        state.isLoading = false;
        state.abortController = null;
      });
  }

  function fetchProducts(page, append) {
    /* Status filter active: serve from the in-memory pool rather than refetching. */
    if (state.filters.status) {
      if (append) {
        renderStatusSlice(true);
        return;
      }
      return fetchAllForStatus();
    }

    /* Filter cleared — drop the pool so it cannot leak into a later render. */
    statusPool = [];
    statusShown = 0;

    /* Abort any in-flight request */
    if (state.abortController) {
      state.abortController.abort();
    }
    state.abortController = new AbortController();

    if (!append) {
      state.isLoading = true;
      showSkeletons(6);
    } else {
      state.isLoadingMore = true;
      appendSkeletons(3);
    }

    setLoadMoreVisible(false);

    var url = buildFetchUrl(page);

    fetch(url, {
      signal: state.abortController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        /* Extract product cards */
        var container = doc.getElementById('api-products');
        var cards = container ? Array.from(container.children) : [];

        /* Extract metadata */
        var fetchMetaEl = doc.getElementById('api-meta');
        var meta = {
          currentPage: page,
          totalPages: 1,
          totalProducts: 0,
          hasNextPage: false
        };
        if (fetchMetaEl) {
          try { meta = JSON.parse(fetchMetaEl.textContent); } catch (e) { /* ignore */ }
        }

        /* Update state */
        state.currentPage = meta.currentPage;
        state.totalPages = meta.totalPages;
        state.totalProducts = meta.totalProducts;

        /*
         * Refresh the Search & Discovery dropdowns so their values and counts
         * describe what was just loaded. Only on a fresh load — appending a page
         * must not rebuild the dropdowns underneath the shopper.
         */
        if (!append && meta.filters) {
          renderShopifyFilters(meta.filters);
        }

        /* Inject cards */
        if (!append) {
          grid.innerHTML = '';
        } else {
          removeAppendSkeletons();
        }

        if (cards.length === 0 && !append) {
          grid.innerHTML =
            '<div class="product-catalogue__empty">' +
            '  <p>Geen producten gevonden.</p>' +
            '</div>';
        }

        var fragment = document.createDocumentFragment();
        var newElements = [];
        cards.forEach(function (card) {
          var imported = document.importNode(card, true);
          newElements.push(imported);
          fragment.appendChild(imported);
        });
        grid.appendChild(fragment);

        updateCount();
        setLoadMoreVisible(meta.hasNextPage);
        animateNewCards(newElements);
        bindCartButtons();
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.error('[catalogue-api] Fetch error:', err);

        if (!append) {
          grid.innerHTML =
            '<div class="product-catalogue__empty">' +
            '  <p>Er is iets misgegaan bij het laden.</p>' +
            '  <button class="product-catalogue__retry-btn" id="catalogue-retry">Opnieuw proberen</button>' +
            '</div>';
          var retryBtn = document.getElementById('catalogue-retry');
          if (retryBtn) {
            retryBtn.addEventListener('click', function () {
              fetchProducts(1, false);
            });
          }
        } else {
          removeAppendSkeletons();
        }
      })
      .finally(function () {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.abortController = null;
      });
  }

  /* ══════════════════════════════════════════════════
     SEARCH
     ══════════════════════════════════════════════════

     Search results are rendered by Liquid via templates/search.api.liquid and
     injected by the same fetchProducts path as the collection grid, so prices,
     was/now strikethroughs and discount badges all come from product-card.

     This previously called /search/suggest.json and built the cards in JS,
     formatting prices with parseInt(price) / 100. Shopify returns that price as
     a decimal string in major units, not cents, so a EUR 46.835,00 product was
     displayed as EUR 468,35. Removing the JS money maths removes the bug and
     keeps one rendering path.
  */

  var searchTimeout = null;

  function handleSearch(query) {
    /* Too short or cleared: drop back to the selected collection. */
    if (!query || query.length < 2) {
      if (!state.searchQuery) return;
      state.searchQuery = '';
      state.currentPage = 1;
      fetchProducts(1, false);
      return;
    }

    state.searchQuery = query;
    state.currentPage = 1;

    /*
     * Searching ignores the collection and the filters by design, so clear
     * their state and labels rather than leaving them looking applied.
     */
    state.shopifyFilters = {};
    state.filters.type = '';
    state.filters.status = '';
    if (typeof clearTagButtons === 'function') clearTagButtons();
    resetShopifyFilterLabels();
    resetFilterLabel('status', 'Beschikbaarheid');

    fetchProducts(1, false);
  }


  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ══════════════════════════════════════════════════
     DROPDOWN HELPERS
     ══════════════════════════════════════════════════ */

  function closeAllDropdowns() {
    filterSelects.forEach(function (s) { s.classList.remove('is-open'); });
    if (sortSelect) sortSelect.classList.remove('is-open');
  }

  document.addEventListener('click', closeAllDropdowns);

  document.querySelectorAll('.product-catalogue__select-dropdown').forEach(function (d) {
    d.addEventListener('click', function (e) { e.stopPropagation(); });
  });

  /* ══════════════════════════════════════════════════
     FILTER DROPDOWNS
     ══════════════════════════════════════════════════ */

  /*
   * Split out so it can be called again after the Categorie / Merk options are
   * rebuilt following a collection switch — freshly injected <a> elements have
   * no listeners of their own.
   */
  function bindFilterOptions(select, key) {
    var label = select.querySelector('[data-filter-label]');
    var options = Array.from(select.querySelectorAll('[data-filter-value]'));

    options.forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var rawValue = opt.dataset.filterValue; /* lowercase slug, handle, or "" */

        if (key === 'collection') {
          /*
           * Switching collection invalidates the other filters: a category or
           * brand from the previous collection may not exist in the new one, and
           * leaving them applied would silently return nothing. So reset them,
           * then rebuild their option lists from the response.
           */
          if (!rawValue || rawValue === state.collection) {
            closeAllDropdowns();
            return;
          }
          state.collection = rawValue;
          state.shopifyFilters = {};
          state.filters.type = '';
          state.filters.status = '';
          if (typeof clearTagButtons === 'function') clearTagButtons();
          resetShopifyFilterLabels();
          resetFilterLabel('status', 'Beschikbaarheid');
        } else if (key === 'shopify') {
          /*
           * Categorie / Merk and anything else configured in Search & Discovery.
           * Shopify gives the exact value to send, so it is used verbatim rather
           * than being derived from the label — the label carries a product
           * count, and category names differ from their filter values.
           */
          var param = select.getAttribute('data-filter-param');
          if (param) {
            if (rawValue) {
              state.shopifyFilters[param] = rawValue;
            } else {
              delete state.shopifyFilters[param];
            }
          }
        } else if (key === 'status') {
          state.filters[key] = rawValue;
        } else {
          state.filters[key] = rawValue ? opt.textContent.trim() : '';
        }

        if (label) label.textContent = opt.textContent;
        state.currentPage = 1;

        /* Clear search when applying a filter */
        if (searchInput && state.searchQuery) {
          searchInput.value = '';
          state.searchQuery = '';
        }

        fetchProducts(1, false);
        closeAllDropdowns();

        options.forEach(function (o) { o.classList.remove('is-active'); });
        opt.classList.add('is-active');
      });
    });
  }

  function resetFilterLabel(key, text) {
    var select = filterSelects.filter(function (s) {
      return s.getAttribute('data-filter-key') === key;
    })[0];
    if (!select) return;
    var label = select.querySelector('[data-filter-label]');
    if (label) label.textContent = text;
    select.querySelectorAll('[data-filter-value]').forEach(function (o) {
      o.classList.remove('is-active');
    });
  }

  filterSelects.forEach(function (select) {
    var key = select.dataset.filterKey;
    var trigger = select.querySelector('[data-filter-trigger]');

    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = select.classList.contains('is-open');
        closeAllDropdowns();
        if (!isOpen) select.classList.add('is-open');
      });
    }

    bindFilterOptions(select, key);
  });

  /* ══════════════════════════════════════════════════
     SORT DROPDOWN
     ══════════════════════════════════════════════════ */

  if (sortSelect) {
    var sortTrigger = sortSelect.querySelector('[data-sort-trigger]');
    var sortLabel = sortSelect.querySelector('[data-sort-label]');
    var sortOptions = Array.from(sortSelect.querySelectorAll('[data-sort-value]'));

    sortTrigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var isOpen = sortSelect.classList.contains('is-open');
      closeAllDropdowns();
      if (!isOpen) sortSelect.classList.add('is-open');
    });

    sortOptions.forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        state.sort = opt.dataset.sortValue;
        sortLabel.textContent = opt.textContent;
        state.currentPage = 1;
        fetchProducts(1, false);
        closeAllDropdowns();
        sortOptions.forEach(function (o) { o.classList.remove('is-active'); });
        opt.classList.add('is-active');
      });
    });
  }

  /* ══════════════════════════════════════════════════
     SEARCH INPUT
     ══════════════════════════════════════════════════ */

  if (searchInput) {
    searchInput.addEventListener('input', function () {
      clearTimeout(searchTimeout);
      var q = this.value.trim().toLowerCase();
      searchTimeout = setTimeout(function () {
        handleSearch(q);
      }, SEARCH_DEBOUNCE_MS);
    });
  }

  /* ══════════════════════════════════════════════════
     TAG BUTTONS
     ══════════════════════════════════════════════════ */

  /*
   * These buttons are shortcuts for the Categorie filter — the product's Type.
   *
   * They previously set state.tag, which was sent as filter.p.tag and never
   * matched: "Indoor" is a product Type, not a tag. They now write to
   * state.filters.type, the same field the Categorie dropdown uses, so the two
   * controls cannot contradict each other and only one request shape exists.
   *
   * data-product-type is stamped by the page templates (index, collection and
   * page.all-products) with the value exactly as stored on the products. The
   * label is matched against the stored types case-insensitively, but the
   * stored value is what gets sent, because Shopify's product_type filter is
   * case-sensitive and the button labels are free-text theme settings.
   */

  var CHEVRON_SVG =
    '<svg class="product-catalogue__select-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M16.293 8.29297L12 12.586L7.70697 8.29297L6.29297 9.70697L12 15.414L17.707 9.70697L16.293 8.29297Z" fill="black"/>' +
    '</svg>';

  /*
   * Build the option list for one Search & Discovery filter.
   * `values` are Shopify's own: each has label, value and count.
   */
  /*
   * Shopify returns filter VALUES already HTML-escaped, while labels come back
   * raw. For "B&B Italia" the API gives:
   *
   *   { "label": "B&B Italia", "value": "B&amp;B Italia" }
   *
   * Sent as-is that becomes filter.p.vendor=B%26amp%3BB%20Italia, which matches
   * no vendor, so the dropdown said "(31)" and the grid returned nothing. Every
   * other brand worked because none of the others contains an ampersand.
   *
   * Decoding through a textarea handles every entity Shopify might use —
   * &amp; &#39; &quot; — rather than only the one we happened to hit.
   */
  function decodeEntities(str) {
    if (!str) return '';
    var el = document.createElement('textarea');
    el.innerHTML = String(str);
    return el.value;
  }

  function filterOptionsHtml(values) {
    var html = '<a href="#" class="product-catalogue__select-option" data-filter-value="">Alles</a>';
    (values || []).forEach(function (v) {
      if (!v || !v.count) return;
      /*
       * Decode first, then escape for the attribute. The browser undoes that
       * escaping when the markup is parsed, so dataset.filterValue reads back
       * the true value — "B&B Italia" — ready for encodeURIComponent.
       */
      html += '<a href="#" class="product-catalogue__select-option' +
              (v.active ? ' is-active' : '') + '" data-filter-value="' +
              escapeHtml(decodeEntities(v.value)) + '">' +
              escapeHtml(v.label) + ' (' + v.count + ')</a>';
    });
    return html;
  }

  /*
   * Render or refresh the Categorie / Merk dropdowns from the fetch response.
   *
   * Two situations:
   *   - the page had no collection context (homepage, All products), so Liquid
   *     could not render them and left an empty slot to fill
   *   - the collection changed, so the existing lists describe the wrong
   *     collection and must be replaced
   */
  function renderShopifyFilters(filters) {
    if (!filters || !filters.length) return;

    var slot = document.querySelector('[data-shopify-filters-slot]');

    filters.forEach(function (f) {
      if (!f || !f.param) return;

      /*
       * Product type must be enabled in Search & Discovery for the Indoor /
       * Outdoor buttons to work — Shopify ignores filter parameters for filters
       * that are not configured — but it gets no dropdown of its own, because
       * the buttons already cover it. Mirrors the same skip in
       * product-catalogue.liquid.
       */
      if (f.param.indexOf('product_type') > -1) return;

      var select = document.querySelector('[data-filter-param="' + f.param + '"]');

      if (!select) {
        if (!slot) return;
        select = document.createElement('div');
        select.className = 'product-catalogue__select';
        select.setAttribute('data-filter-select', '');
        select.setAttribute('data-filter-key', 'shopify');
        select.setAttribute('data-filter-param', f.param);
        select.setAttribute('data-filter-default-label', f.label || '');
        select.innerHTML =
          '<button class="product-catalogue__select-trigger" data-filter-trigger>' +
          '  <span class="product-catalogue__select-text" data-filter-label>' + escapeHtml(f.label) + '</span>' +
          CHEVRON_SVG +
          '</button>' +
          /*
           * data-lenis-prevent is required: the theme runs Lenis smooth
           * scrolling, which otherwise swallows wheel events over the list and
           * the long brand list could not be scrolled.
           */
          '<div class="product-catalogue__select-dropdown" data-lenis-prevent="true"></div>';
        slot.appendChild(select);

        /* New element: it needs the open/close handler and the option handlers. */
        var trigger = select.querySelector('[data-filter-trigger]');
        if (trigger) {
          trigger.addEventListener('click', function (e) {
            e.stopPropagation();
            var isOpen = select.classList.contains('is-open');
            closeAllDropdowns();
            if (!isOpen) select.classList.add('is-open');
          });
        }
        select.querySelector('.product-catalogue__select-dropdown')
              .addEventListener('click', function (e) { e.stopPropagation(); });

        if (filterSelects.indexOf(select) === -1) filterSelects.push(select);
      }

      var dropdown = select.querySelector('.product-catalogue__select-dropdown');
      if (!dropdown) return;
      dropdown.innerHTML = filterOptionsHtml(f.values);
      bindFilterOptions(select, 'shopify');
    });

    if (slot) slot.removeAttribute('data-filters-pending');
  }

  /* Put every Search & Discovery dropdown back to its unfiltered label. */
  function resetShopifyFilterLabels() {
    document.querySelectorAll('[data-filter-key="shopify"]').forEach(function (select) {
      select.querySelectorAll('[data-filter-value]').forEach(function (o) {
        o.classList.remove('is-active');
      });
      /* Labels are Shopify's own ("Categorie", "Merk"), stored when rendered. */
      var label = select.querySelector('[data-filter-label]');
      var original = select.getAttribute('data-filter-default-label');
      if (label && original) label.textContent = original;
    });
  }

  /*
   * syncTypeDropdown removed. It kept the Categorie dropdown in step with the
   * Indoor / Outdoor buttons while that dropdown listed Product types. Categorie
   * now lists Shopify's Category, a different dimension, so there is nothing to
   * mirror — the buttons and the dropdown filter independently and can both be
   * applied at once.
   */

  function clearTagButtons() {
    tagButtons.forEach(function (t) { t.classList.remove('is-active'); });
  }

  tagButtons.forEach(function (tagBtn) {
    tagBtn.addEventListener('click', function (e) {
      e.preventDefault();

      var typeValue = tagBtn.getAttribute('data-product-type');
      if (!typeValue) {
        /* Label matches no product type in this collection — nothing to filter. */
        console.warn('[catalogue-api] Quick filter "' + tagBtn.textContent.trim() +
                     '" does not match any product type in this collection. ' +
                     'Check Theme settings against the products\' Type field.');
        return;
      }

      if (tagBtn.classList.contains('is-active')) {
        tagBtn.classList.remove('is-active');
        state.filters.type = '';
      } else {
        clearTagButtons();
        tagBtn.classList.add('is-active');
        state.filters.type = typeValue;
      }

      /* Clear search */
      if (searchInput && state.searchQuery) {
        searchInput.value = '';
        state.searchQuery = '';
      }

      state.currentPage = 1;
      fetchProducts(1, false);
    });
  });

  /* ══════════════════════════════════════════════════
     LOAD MORE
     ══════════════════════════════════════════════════ */

  if (loadMoreBtn) {
    /* It is a <div>, so give it button semantics for keyboard and screen readers. */
    if (!/^(A|BUTTON)$/.test(loadMoreBtn.tagName)) {
      loadMoreBtn.setAttribute('role', 'button');
      loadMoreBtn.setAttribute('tabindex', '0');
      loadMoreBtn.style.cursor = 'pointer';
    }

    function doLoadMore(e) {
      if (e) e.preventDefault();
      if (state.isLoadingMore || state.currentPage >= state.totalPages) return;
      fetchProducts(state.currentPage + 1, true);
    }

    loadMoreBtn.addEventListener('click', doLoadMore);
    loadMoreBtn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') doLoadMore(e);
    });
  }

  /* ══════════════════════════════════════════════════
     INITIALISE — products are already server-rendered,
     no fetch needed. Just read state and bind events.
     ══════════════════════════════════════════════════ */

  updateCount();
  bindCartButtons();

  /*
   * Categorie and Merk come from collection.filters, which Shopify only
   * populates on a collection or search request. The homepage and the All
   * products page are neither, so Liquid left an empty slot — fill it from the
   * fetch endpoint, which does run in a collection context.
   *
   * Deferred until the browser is idle so it never competes with rendering the
   * page. The grid is already on screen; only the dropdowns are waiting.
   */
  (function populatePendingFilters() {
    var slot = document.querySelector('[data-shopify-filters-slot][data-filters-pending]');
    if (!slot) return;

    function load() {
      fetchPage(1, null)
        .then(function (result) {
          if (result && result.meta && result.meta.filters) {
            renderShopifyFilters(result.meta.filters);
          }
        })
        .catch(function (err) {
          console.warn('[catalogue-api] Could not load the filter options:', err);
        });
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load, { timeout: 2000 });
    } else {
      window.setTimeout(load, 600);
    }
  })();

})();
